import {
  CalendarDate,
  DateFormatter,
  getLocalTimeZone
} from '@internationalized/date';
import { rd } from '@passionware/monads';
import { useMemo } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import type { Resource } from '__types__';
import { selectActiveAccount } from 'store/accounts';
import {
  getPublicBrandImage,
  getPublicCompanyName
} from 'store/v1/auth/auth.selectors';
import { Button } from 'v5/design-system/Button';
import { SimpleTooltip } from 'v5/design-system/Tooltip';
import { UserAvatar } from 'v5/patterns/widgets/user-avatar/UserAvatar';
import { WithServices } from 'v5/platform/ts/services';
import { WithAppScopeService } from 'v5/services/internal/AppScopeService/AppScopeService';
import { WithSystemSettingsService } from 'v5/services/internal/SystemSettingsService/SystemSettingsService';

export type RequestAvailabilityEmailConfirmProps = WithServices<
  [WithAppScopeService, WithSystemSettingsService]
> & {
  startDate: CalendarDate;
  endDate: CalendarDate;
  message: string;
  recipients: Resource[];
};

export function RequestAvailabilityEmailConfirm({
  startDate,
  endDate,
  message,
  recipients,
  services
}: RequestAvailabilityEmailConfirmProps) {
  const { t } = useTranslation('availability-request', {
    keyPrefix: 'RequestAvailabilityDialog'
  });

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-[minmax(0,1fr)_minmax(0,3fr)] items-start gap-x-4 gap-y-1">
        <div>
          <p className="text-base font-semibold text-fg-default">
            {t('emailPreviewTitle')}
          </p>
          <p className="text-base text-fg-muted">{t('emailPreviewHelper')}</p>
        </div>

        <EmailPreviewFacsimile
          startDate={startDate}
          endDate={endDate}
          message={message}
          exampleRecipient={recipients[0]}
          services={services}
        />
      </div>

      <div className="grid grid-cols-[minmax(0,1fr)_minmax(0,3fr)] items-start gap-x-4 gap-y-1">
        <p className="text-base font-semibold text-fg-default">
          {t('recipients')}
        </p>
        <div className="flex flex-wrap gap-2">
          {recipients.map(resource => (
            <SimpleTooltip key={resource.id} title={resource.full_name}>
              <UserAvatar
                user={rd.of({
                  avatarUrl: resource.profile_picture,
                  name: resource.full_name
                })}
                size="sm"
              />
            </SimpleTooltip>
          ))}
        </div>
      </div>
    </div>
  );
}

function EmailPreviewFacsimile({
  startDate,
  endDate,
  message,
  exampleRecipient,
  services
}: {
  startDate: CalendarDate;
  endDate: CalendarDate;
  message: string;
  exampleRecipient: Resource | undefined;
  services: RequestAvailabilityEmailConfirmProps['services'];
}) {
  const { i18n } = useTranslation();
  // Workspace public language (mail / resource portal), not staff preferredLanguage.
  const scope = services.appScopeService.useResolvedScopeIdentity();
  const localeRd = services.systemSettingsService.useSetting(
    scope,
    'defaults_locale'
  );
  const locale = rd.mapOrElse(localeRd, setting => setting.value, 'en');
  const t = useMemo(
    () =>
      i18n.getFixedT(
        locale,
        'availability-request',
        'RequestAvailabilityDialog'
      ),
    [i18n, locale]
  );
  const companyName = useSelector(getPublicCompanyName) ?? '';
  const brandImage = useSelector(getPublicBrandImage);
  const activeAccount = useSelector(selectActiveAccount);
  const senderName = contextualSenderName(activeAccount, companyName);
  const dateFormatter = useMemo(
    () =>
      new DateFormatter(locale, {
        month: 'long',
        day: 'numeric'
      }),
    [locale]
  );
  const timezone = getLocalTimeZone();
  const formattedStart = dateFormatter.format(startDate.toDate(timezone));
  const formattedEnd = dateFormatter.format(endDate.toDate(timezone));
  const recipientFirstName = exampleRecipient
    ? resourceFirstName(exampleRecipient)
    : '';
  const trimmedMessage = message.trim();

  return (
    <div className="overflow-hidden rounded-lg border border-grey bg-white">
      <div className="border-b border-grey px-4 py-3 text-center">
        <img
          src={brandImage || '/images/atellio_brand_logomark.svg'}
          alt=""
          className="mx-auto h-8 w-auto max-h-8 object-contain"
        />
      </div>
      <div className="space-y-4 px-4 py-4 text-base text-charcoal">
        <div className="space-y-1">
          <p>{t('emailPreviewGreeting', { name: recipientFirstName })}</p>
          <p>
            {t('emailPreviewBody', {
              sender: senderName,
              company: companyName
            })}
          </p>
        </div>
        {trimmedMessage ? (
          <p className="whitespace-pre-wrap">{trimmedMessage}</p>
        ) : null}
        <div className="rounded-md border border-grey px-3 py-2.5">
          <p className="text-sm text-grey-dark">
            {t('emailPreviewDatesLabel')}
          </p>
          <p>
            <Trans
              t={t}
              i18nKey="emailPreviewDateRange"
              values={{
                startDate: formattedStart,
                endDate: formattedEnd
              }}
              components={{
                start: <strong />,
                end: <strong />
              }}
            />
          </p>
        </div>
        <div className="space-y-3">
          <p>{t('emailPreviewCtaHint')}</p>
          <Button
            type="button"
            variant="headless"
            disabled
            className="pointer-events-none rounded bg-charcoal px-4 py-2 text-white"
          >
            {t('emailPreviewShareButton')}
          </Button>
        </div>
        <p className="text-sm text-grey-dark">{t('emailPreviewExpiry')}</p>
      </div>
    </div>
  );
}

function resourceFirstName(resource: Resource): string {
  const firstName = resource.first_name?.trim();
  if (firstName) {
    return firstName;
  }
  return resource.full_name.trim().split(/\s+/)[0] ?? resource.full_name;
}

/** Matches backend `contextualSenderName`: "Ada Lovelace (Atellio)". */
function contextualSenderName(
  account: { firstName?: string; lastName?: string } | null | undefined,
  companyName: string
): string {
  const accountName = [account?.firstName, account?.lastName]
    .filter(Boolean)
    .join(' ');
  if (accountName && companyName) {
    return `${accountName} (${companyName})`;
  }
  return accountName || companyName;
}
